import {useEffect, useRef, useState} from 'react';
import {Box, Button, HStack, Text, useColorModeValue, useToast, VStack,} from '@chakra-ui/react';
import {Team} from 'types';
import {draggable, dropTargetForElements} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {combine} from '@atlaskit/pragmatic-drag-and-drop/combine';
import {reorder} from '@atlaskit/pragmatic-drag-and-drop/reorder';
import {useManualUpdateRankingsMutation} from '../../../../app/api';

interface ManualRankingEditorProps {
  teams: Team[];
  eventId: number;
  leagueId: number;
  onClose: () => void;
  onSave: () => void;
}

interface DragData extends Record<string, unknown> {
  index: number;
  teamId: number;
}

export const ManualRankingEditor: React.FC<ManualRankingEditorProps> = ({
                                                                          teams,
                                                                          eventId,
                                                                          leagueId,
                                                                          onClose,
                                                                          onSave,
                                                                        }) => {
  const [orderedTeams, setOrderedTeams] = useState<Team[]>([]);
  const [currentlyDraggingIndex, setCurrentlyDraggingIndex] = useState<number | null>(null);
  const dropContainerRef = useRef<HTMLDivElement>(null);
  const buttonColor = useColorModeValue('orange', 'blue');
  const toast = useToast();


  const [manualUpdateRankings, {isLoading: isSubmitting}] = useManualUpdateRankingsMutation();

  useEffect(() => {
    setOrderedTeams([...teams]);
  }, [teams]);

  useEffect(() => {
    const dropContainer = dropContainerRef.current;
    if (!dropContainer) return;

    return dropTargetForElements({
      element: dropContainer,
      onDrag: ({source}) => {
        const sourceData = source.data as DragData;
        setCurrentlyDraggingIndex(sourceData.index);
      },
      onDrop: ({source, location}) => {
        const sourceData = source.data as DragData;
        const sourceIndex = sourceData.index;

        const closestTarget = location.current.dropTargets[0];
        if (!closestTarget) {
          setCurrentlyDraggingIndex(null);
          return;
        }

        const targetData = closestTarget.data as unknown as DragData;
        const targetIndex = targetData.index;

        if (sourceIndex !== undefined && targetIndex !== undefined && sourceIndex !== targetIndex) {
          setOrderedTeams((currentTeams) => {
            return reorder({
              list: currentTeams,
              startIndex: sourceIndex,
              finishIndex: targetIndex,
            });
          });
        }

        setCurrentlyDraggingIndex(null);
      },
      onDragLeave: () => {
        setCurrentlyDraggingIndex(null);
      },
    });
  }, []);

  const handleSaveRankings = async () => {
    try {
      const rankings = orderedTeams.map((team, index) => ({
        teamId: team.id,
        rank: index + 1,
      }));

      await manualUpdateRankings({
        eventId,
        leagueId,
        setManualRankingRequest: {rankings},
      }).unwrap();

      toast({
        title: 'Success',
        description: 'Team rankings have been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSave();
    } catch (error) {
      const errorMessage = (error as any)?.data?.message || 'Failed to update rankings';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <VStack ref={dropContainerRef} spacing={2} align="stretch">
        {orderedTeams.map((team, index) => (
          <DraggableTeamItem
            key={team.id}
            team={team}
            index={index}
            isDragging={currentlyDraggingIndex === index}
          />
        ))}
      </VStack>

      <HStack justify="flex-end" pt={4}>
        <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          colorScheme={buttonColor}
          onClick={handleSaveRankings}
          isLoading={isSubmitting}
          loadingText="Saving..."
        >
          Save Rankings
        </Button>
      </HStack>
    </VStack>
  );
};

interface DraggableTeamItemProps {
  team: Team;
  index: number;
  isDragging: boolean;
}

const DraggableTeamItem: React.FC<DraggableTeamItemProps> = ({
                                                               team,
                                                               index,
                                                               isDragging,
                                                             }) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dragHandle = dragHandleRef.current;
    const item = itemRef.current;
    if (!dragHandle || !item) return;

    return combine(
      draggable({
        element: dragHandle,
        getInitialData: () =>
          ({
            index,
            teamId: team.id,
          }) as Record<string, unknown>,
      }),
      dropTargetForElements({
        element: item,
        getData: () =>
          ({
            index,
            teamId: team.id,
          }) as Record<string, unknown>,
      })
    );

  }, [index, team.id]);

  const displayRank = index + 1;
  const teamRecord = `${team.gamesWon}-${team.gamesLost}${team.gamesTied ? `-${team.gamesTied}` : ''}`;

  return (
    <Box
      ref={itemRef}
      borderWidth={1}
      borderRadius="md"
      bg={isDragging ? 'gray.100' : 'white'}
      opacity={isDragging ? 0.6 : 1}
      transition="opacity 0.2s, background-color 0.2s"
      _hover={{bg: isDragging ? 'gray.100' : 'gray.50'}}
    >
      <HStack p={3} spacing={3}>
        <Box
          ref={dragHandleRef}
          cursor="grab"
          _active={{cursor: 'grabbing'}}
          px={2}
          py={1}
          borderRadius="md"
          _hover={{bg: 'gray.200'}}
        >
          <Text fontSize="lg" fontWeight="bold" color="gray.600">
            ⋮⋮
          </Text>
        </Box>

        <HStack flex={1} justify="space-between">
          <HStack spacing={2}>
            <Text fontWeight="bold" minW="30px">
              {displayRank}.
            </Text>
            <Text>
              {team.name}
            </Text>
          </HStack>
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            {teamRecord}
          </Text>
        </HStack>
      </HStack>
    </Box>
  );
};
